/**
 * Type Decorators for ECS Components and Systems
 * ECS 组件和系统的类型装饰器
 *
 * Provides decorators to mark component/system types with stable names
 * that survive code minification.
 *
 * 提供装饰器为组件/系统类型标记稳定的名称，使其在代码混淆后仍然有效。
 */
import { GlobalComponentRegistry } from '../Core/ComponentStorage/ComponentRegistry';
import { COMPONENT_TYPE_NAME, COMPONENT_DEPENDENCIES, COMPONENT_EDITOR_OPTIONS, getWritableComponentTypeMetadata } from '../Core/ComponentStorage/ComponentTypeUtils';
import { SYNC_METADATA } from '../Sync/types';
/**
 * 存储系统类型名称的Symbol键
 * Symbol key for storing system type name
 */
export const SYSTEM_TYPE_NAME = Symbol('SystemTypeName');
/**
 * 获取系统类型元数据
 * Get system type metadata
 */
function getSystemTypeMetadata(systemType) {
    return systemType;
}
/**
 * 获取可写的系统类型元数据
 * Get writable system type metadata
 */
function getWritableSystemTypeMetadata(systemType) {
    return systemType;
}
/**
 * 组件类型装饰器
 * Component type decorator
 *
 * 用于为组件类指定固定的类型名称，避免在代码混淆后失效。
 * 装饰器执行时会自动注册到 ComponentRegistry，使组件可以通过名称反序列化。
 *
 * Assigns a stable type name to component classes that survives minification.
 * The decorator automatically registers to ComponentRegistry, enabling deserialization by name.
 *
 * @param typeName 组件类型名称 | Component type name
 * @param options 组件配置选项 | Component options
 * @example
 * ```typescript
 * @ECSComponent('Position')
 * class PositionComponent extends Component {
 *     x: number = 0;
 *     y: number = 0;
 * }
 *
 * // 带依赖声明 | With dependency declaration
 * @ECSComponent('SpriteAnimator', { requires: ['Sprite'] })
 * class SpriteAnimatorComponent extends Component {
 *     // ...
 * }
 * ```
 */
export function ECSComponent(typeName, options) {
    return function (target) {
        if (!typeName || typeof typeName !== 'string') {
            throw new Error('ECSComponent装饰器必须提供有效的类型名称');
        }
        // 获取可写的元数据对象
        // Get writable metadata object
        const metadata = getWritableComponentTypeMetadata(target);
        // 在构造函数上存储类型名称
        // Store type name on constructor
        metadata[COMPONENT_TYPE_NAME] = typeName;
        // 存储依赖关系
        // Store dependencies
        if (options?.requires) {
            metadata[COMPONENT_DEPENDENCIES] = options.requires;
        }
        // 存储编辑器选项
        // Store editor options
        if (options?.editor) {
            metadata[COMPONENT_EDITOR_OPTIONS] = options.editor;
        }
        // 更新 @sync 装饰器创建的 SYNC_METADATA.typeId（如果存在）
        // Update SYNC_METADATA.typeId created by @sync decorator (if exists)
        // Property decorators execute before class decorators, so @sync may have used constructor.name
        const syncMeta = target[SYNC_METADATA];
        if (syncMeta) {
            syncMeta.typeId = typeName;
        }
        // 自动注册到全局 ComponentRegistry，使组件可以通过名称查找
        // Auto-register to GlobalComponentRegistry, enabling lookup by name
        GlobalComponentRegistry.register(target);
        return target;
    };
}
/**
 * 系统类型装饰器
 * System type decorator
 *
 * 用于为系统类指定固定的类型名称，避免在代码混淆后失效。
 * Assigns a stable type name to system classes that survives minification.
 *
 * @param typeName 系统类型名称 | System type name
 * @param metadata 系统元数据配置 | System metadata configuration
 * @example
 * ```typescript
 * @ECSSystem('Movement')
 * class MovementSystem extends EntitySystem {
 *     protected process(entities: Entity[]): void {
 *         // 系统逻辑
 *     }
 * }
 *
 * @ECSSystem('Physics', { updateOrder: 10 })
 * class PhysicsSystem extends EntitySystem {
 *     // ...
 * }
 * ```
 */
export function ECSSystem(typeName, metadata) {
    return function (target) {
        if (!typeName || typeof typeName !== 'string') {
            throw new Error('ECSSystem装饰器必须提供有效的类型名称');
        }
        // 获取可写的元数据对象
        // Get writable metadata object
        const meta = getWritableSystemTypeMetadata(target);
        // 在构造函数上存储类型名称
        // Store type name on constructor
        meta[SYSTEM_TYPE_NAME] = typeName;
        // 存储元数据
        // Store metadata
        if (metadata) {
            meta.__systemMetadata__ = metadata;
        }
        return target;
    };
}
/**
 * 获取 System 的元数据
 * Get System metadata
 */
export function getSystemMetadata(systemType) {
    const meta = getSystemTypeMetadata(systemType);
    return meta.__systemMetadata__;
}
/**
 * 从系统实例获取元数据
 * Get metadata from system instance
 *
 * @param system 系统实例 | System instance
 * @returns 系统元数据 | System metadata
 */
export function getSystemInstanceMetadata(system) {
    return getSystemMetadata(system.constructor);
}
/**
 * 获取系统类型的名称，优先使用装饰器指定的名称
 * Get system type name, preferring decorator-specified name
 *
 * @param systemType 系统构造函数 | System constructor
 * @returns 系统类型名称 | System type name
 */
export function getSystemTypeName(systemType) {
    const meta = getSystemTypeMetadata(systemType);
    const decoratorName = meta[SYSTEM_TYPE_NAME];
    if (decoratorName) {
        return decoratorName;
    }
    return systemType.name || 'UnknownSystem';
}
/**
 * 从系统实例获取类型名称
 * Get type name from system instance
 *
 * @param system 系统实例 | System instance
 * @returns 系统类型名称 | System type name
 */
export function getSystemInstanceTypeName(system) {
    return getSystemTypeName(system.constructor);
}
//# sourceMappingURL=TypeDecorators.js.map